import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { resolveFileUrl } from "../lib/urls";
import ReelBrandingCard from "../components/ReelBrandingCard";
import RSVPListCard from "../components/RSVPListCard";
import ZipProgressCard from "../components/ZipProgressCard";
import QrPosterPicker from "../components/QrPosterPicker";
import { Download, Trash2, Check, X as XIcon, ExternalLink, ArrowLeft, Copy, Image as ImageIcon, Video, MessageSquare, Settings, Sparkles, Wand2 } from "lucide-react";
import ExpiryBanner from "../components/ExpiryBanner";
import GuestListSection from "../components/GuestListSection";
import FaceGroupingCard from "../components/FaceGroupingCard";
import TopLikedPhotosWidget from "../components/TopLikedPhotosWidget";
import RenewBanner from "../components/RenewBanner";

const EventManagePage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [tab, setTab] = useState("gallery");
  const [qrUrl, setQrUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const [e, u] = await Promise.all([
      api.get(`/events/${id}`),
      api.get(`/events/${id}/uploads`)
    ]);
    setEvent(e.data); setUploads(u.data);
    setQrUrl(`${API}/public/events/${e.data.slug}/qr?base_url=${encodeURIComponent(window.location.origin)}`);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Return-from-Stripe (addon checkout): poll the addon status endpoint so
  // the guest-list feature flag flips on immediately even if the Stripe
  // webhook lands after the redirect. Idempotent — always safe to call.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("addon_session_id");
    if (!sid) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/addons/status/${sid}`);
        if (r.data?.payment_status === "paid") {
          clearInterval(iv);
          // Clean the query string so a refresh doesn't re-poll.
          window.history.replaceState({}, "", window.location.pathname);
          await load();
        }
      } catch { /* keep polling */ }
      if (attempts > 20) clearInterval(iv);
    }, 1500);
    return () => clearInterval(iv);
  }, [load]);

  const guestLink = event ? `${window.location.origin}/g/${event.slug}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(guestLink);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const delUpload = async (uid) => {
    if (!window.confirm("Delete this upload?")) return;
    await api.delete(`/events/${id}/uploads/${uid}`);
    setUploads(uploads.filter(u => u.id !== uid));
  };

  const approve = async (uid, val) => {
    await api.patch(`/events/${id}/uploads/${uid}/approve?approved=${val}`);
    setUploads(uploads.map(u => u.id === uid ? { ...u, approved: val } : u));
  };

  const [heicRepairing, setHeicRepairing] = useState(false);
  const heicCount = uploads.filter(u => u.kind === "photo" && /\.hei[fc]$/i.test(u.file_url || "")).length;
  const repairHeic = async () => {
    setHeicRepairing(true);
    try {
      const res = await api.post(`/events/${id}/uploads/repair-heic`);
      const { converted, skipped, failed } = res.data;
      alert(`Repair complete\n• Converted: ${converted}\n• Skipped: ${skipped}\n• Failed: ${failed}\n\nReloading gallery…`);
      await load();
    } catch (err) {
      alert("Repair failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setHeicRepairing(false);
    }
  };

  const toggleSetting = async (key, val) => {
    const res = await api.patch(`/events/${id}`, { [key]: val });
    setEvent(res.data);
  };

  const [zipBusy, setZipBusy] = useState(false);
  const [posterPickerOpen, setPosterPickerOpen] = useState(false);
  // "idle" | "preparing" | "downloading" | "done"
  const [zipStage, setZipStage] = useState("idle");
  const [zipProgress, setZipProgress] = useState(0);
  const [zipElapsed, setZipElapsed] = useState(0);

  const downloadZip = async () => {
    if (zipBusy) return;
    const mediaCount = uploads.filter((u) => u.kind === "photo" || u.kind === "video").length;
    setZipBusy(true);
    setZipStage("preparing");
    setZipProgress(0);
    setZipElapsed(0);
    const startedAt = Date.now();
    // Tick every 500ms to keep elapsed time + fake progress bar animating.
    const ticker = setInterval(() => {
      setZipElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 500);
    try {
      const res = await api.get(`/events/${id}/download`, {
        responseType: "blob",
        onDownloadProgress: (e) => {
          setZipStage("downloading");
          if (e.total) {
            setZipProgress(Math.round((e.loaded / e.total) * 100));
          } else if (e.loaded) {
            // Server didn't send Content-Length. Show a linear ramp based on
            // bytes-so-far to at least prove data is arriving.
            setZipProgress((p) => Math.min(95, p + 1));
          }
        },
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${event.slug}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      setZipStage("done");
      setZipProgress(100);
      // Auto-hide the card after 8 seconds
      setTimeout(() => {
        setZipStage("idle");
      }, 8000);
    } catch (err) {
      setZipStage("idle");
      const detail = err?.response?.data?.detail
        || (err?.response?.status ? `HTTP ${err.response.status}` : err.message);
      alert(`Download failed: ${detail}`);
    } finally {
      clearInterval(ticker);
      setZipBusy(false);
    }
    // Media count is only used to render the card label — captured via closure.
    void mediaCount;
  };

  const [reelBusy, setReelBusy] = useState(false);
  const [reelResult, setReelResult] = useState(null); // {count, url}
  const generateHighlights = async () => {
    const photoCount = uploads.filter(u => u.kind === "photo").length;
    if (photoCount < 3) {
      alert("You need at least 3 photos to generate a highlights reel.");
      return;
    }
    setReelBusy(true);
    setReelResult(null);
    try {
      // Kick off curation as an async background task. The endpoint returns
      // 202 {status:"generating"} immediately — we poll /status until ready.
      await api.post(`/events/${id}/highlights/generate?limit=30`);
      const shareUrl = `${window.location.origin}/g/${event.slug}/highlights`;

      const startedAt = Date.now();
      const MAX_WAIT_MS = 4 * 60 * 1000; // 4 min — LLM curation of ~60 photos
      let finalCount = 0;
      // Poll every 3s; back off progressively past 30s so we don't hammer.
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        const wait = Date.now() - startedAt < 30000 ? 3000 : 5000;
        await new Promise((r) => setTimeout(r, wait));
        const st = await api.get(`/events/${id}/highlights/status`);
        if (st.data.status === "ready") {
          finalCount = st.data.count || 0;
          break;
        }
        if (st.data.status === "error") {
          throw new Error(st.data.error || "Curation failed");
        }
      }
      if (finalCount === 0) {
        throw new Error("Reel curation is taking longer than expected. Please refresh and check the highlights page in a minute.");
      }
      setReelResult({ count: finalCount, url: shareUrl });
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not generate highlights reel. Please try again.");
    } finally {
      setReelBusy(false);
    }
  };

  const copyHighlightsLink = () => {
    if (!reelResult?.url) return;
    navigator.clipboard.writeText(reelResult.url);
  };

  const fileUrl = (u) => resolveFileUrl(u.file_url);

  if (!event) return <div className="min-h-[80vh] flex items-center justify-center text-navy-700/60">Loading...</div>;

  const photos = uploads.filter(u => u.kind === "photo");
  const videos = uploads.filter(u => u.kind === "video");
  const messages = uploads.filter(u => u.kind === "message");

  return (
    <section className="min-h-[85vh] bg-cream-100 py-10">
      <div className="max-w-6xl mx-auto px-5">
        <Link to="/dashboard" className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> All events</Link>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">{event.type}</p>
            <h1 className="font-serif italic text-navy-700 text-4xl md:text-5xl mt-1">{event.name}</h1>
            {event.date && <div className="text-sm text-navy-700/60 mt-1">{event.date}</div>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {event.tier !== "grand" && (
              <Link to={`/dashboard/events/${id}/upgrade`} className="inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-coral-400"><Sparkles className="w-4 h-4" /> Upgrade</Link>
            )}
            <button
              onClick={generateHighlights}
              disabled={reelBusy}
              data-testid="generate-highlights-btn"
              className="inline-flex items-center gap-2 bg-white border border-navy-700/20 text-navy-700 px-5 py-2.5 rounded-full font-semibold hover:bg-cream-100 disabled:opacity-60"
            >
              <Wand2 className="w-4 h-4" /> {reelBusy ? "Curating…" : "Generate highlights reel"}
            </button>
            <button
              onClick={downloadZip}
              disabled={zipBusy}
              data-testid="download-zip-btn"
              className="inline-flex items-center gap-2 bg-navy-700 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-navy-800 disabled:opacity-60 disabled:cursor-wait"
            >
              <Download className="w-4 h-4" /> {zipBusy ? "Preparing ZIP…" : "Download all (ZIP)"}
            </button>
          </div>
        </div>

        {zipStage !== "idle" && (
          <ZipProgressCard
            stage={zipStage}
            progress={zipProgress}
            fileCount={uploads.filter((u) => u.kind === "photo" || u.kind === "video").length}
            elapsedSec={zipElapsed}
            estimatedSec={Math.max(
              10,
              // Rough estimate: 1.5s per photo + 4s per video, min 10s.
              Math.round(
                uploads.filter((u) => u.kind === "photo").length * 1.5
                + uploads.filter((u) => u.kind === "video").length * 4
              )
            )}
            onCancel={() => setZipStage("idle")}
          />
        )}

        {reelResult && (
          <div data-testid="highlights-share-banner" className="mb-6 bg-gradient-to-r from-coral-300/15 to-navy-700/10 border border-coral-300/30 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-coral-500 mb-1">Highlights ready ✨</div>
              <div className="text-navy-700 text-sm">
                We picked <b>{reelResult.count}</b> best moments (near-duplicates removed).
                Share this link so guests can relive the day:
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input readOnly value={reelResult.url} className="flex-1 px-3 py-2 bg-white rounded-md text-sm border border-cream-200 min-w-[280px]" data-testid="highlights-share-url" />
                <button onClick={copyHighlightsLink} className="px-3 py-2 bg-coral-300 text-white rounded-md text-sm font-semibold hover:bg-coral-400 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
                <a href={reelResult.url} target="_blank" rel="noreferrer" data-testid="highlights-open-link" className="px-3 py-2 border border-navy-700/20 rounded-md hover:bg-white inline-flex items-center gap-1 text-sm"><ExternalLink className="w-3.5 h-3.5" /> Open</a>
              </div>
            </div>
          </div>
        )}

        {/* Expiry reminder */}
        <ExpiryBanner event={event} />

        {/* Tier badge */}
        <div className="mb-6 bg-white rounded-2xl px-5 py-3 soft-shadow flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${event.tier === "free" ? "bg-cream-200 text-navy-700" : "bg-coral-300/20 text-coral-500"}`}>
              {event.tier} tier
            </span>
            {event.expires_at && (
              <span className="text-navy-700/60">Expires {new Date(event.expires_at).toLocaleDateString()}</span>
            )}
          </div>
          {event.tier === "free" && (
            <Link to={`/dashboard/events/${id}/upgrade`} className="text-coral-300 font-semibold hover:text-coral-400 inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Upgrade for more uploads &amp; longer hosting
            </Link>
          )}
        </div>

        {/* Share + QR */}
        <div className="bg-white rounded-2xl soft-shadow p-6 mb-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-coral-300">Guest Link</div>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={guestLink} className="flex-1 px-3 py-2 bg-cream-100 rounded-md text-sm border border-cream-200" />
              <button onClick={copyLink} className="px-3 py-2 bg-coral-300 text-white rounded-md text-sm font-semibold hover:bg-coral-400 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy"}</button>
              <a href={guestLink} target="_blank" rel="noreferrer" className="px-3 py-2 border border-navy-700/20 rounded-md hover:bg-cream-100"><ExternalLink className="w-4 h-4 text-navy-700" /></a>
            </div>
            <p className="text-xs text-navy-700/60 mt-3">Share this link or display the QR code at your event. Guests don&apos;t need an account.</p>
          </div>
          <div className="text-center">
            <div className="bg-cream-100 p-3 rounded-xl">
              <img src={qrUrl} alt="QR" className="w-40 h-40" />
            </div>
            <button
              onClick={() => setPosterPickerOpen(true)}
              data-testid="download-qr-btn"
              className="text-xs text-coral-300 font-semibold mt-2 inline-block hover:text-coral-400 underline"
            >
              Download QR (choose a poster)
            </button>
            <a
              href={qrUrl}
              download={`${event.slug}-qr.png`}
              className="block text-xs text-navy-700/50 hover:text-navy-700 mt-1"
              data-testid="download-qr-plain-link"
            >
              or plain QR (PNG)
            </a>
          </div>
        </div>

        <QrPosterPicker
          open={posterPickerOpen}
          eventId={id}
          eventSlug={event.slug}
          baseUrl={window.location.origin}
          onClose={() => setPosterPickerOpen(false)}
        />

        {/* Tabs */}
        <div className="flex gap-2 border-b border-cream-200 mb-6">
          {[
            ["gallery", `Gallery (${photos.length + videos.length})`, ImageIcon],
            ["messages", `Guestbook (${messages.length})`, MessageSquare],
            ["settings", "Settings", Settings]
          ].map(([k, lbl, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-3 text-sm font-semibold border-b-2 inline-flex items-center gap-2 ${tab === k ? "border-coral-300 text-coral-500" : "border-transparent text-navy-700/60 hover:text-navy-700"}`}>
              <Icon className="w-4 h-4" /> {lbl}
            </button>
          ))}
        </div>

        {/* Renewal banner — shown automatically when event is expiring or already expired. */}
        <RenewBanner event={event} />

        {/* Guest list add-on — sits above the tabs so the paywall is visible to every host, not buried in Settings. */}
        <GuestListSection event={event} onEventChange={setEvent} />

        {/* Top-liked photos + AI face grouping — value-add widgets, only meaningful once uploads exist. */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <TopLikedPhotosWidget event={event} />
          <FaceGroupingCard event={event} />
        </div>

        {tab === "gallery" && (
          <div>
            {heicCount > 0 && (
              <div
                data-testid="heic-repair-banner"
                className="mb-4 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-amber-900">
                    {heicCount} iPhone photo{heicCount > 1 ? "s" : ""} in HEIC format
                  </div>
                  <div className="text-xs text-amber-800/80">
                    Most browsers can&apos;t display HEIC. Click to convert them all to JPEG so they show for you and your guests.
                  </div>
                </div>
                <button
                  onClick={repairHeic}
                  disabled={heicRepairing}
                  data-testid="heic-repair-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                >
                  {heicRepairing ? "Converting…" : `Fix ${heicCount} photo${heicCount > 1 ? "s" : ""}`}
                </button>
              </div>
            )}
            {photos.length + videos.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl soft-shadow text-navy-700/60">No uploads yet. Share the link with your guests!</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...photos, ...videos].map(u => (
                  <div key={u.id} className="relative group rounded-xl overflow-hidden bg-cream-200 aspect-square">
                    {u.kind === "photo" ? (
                      <img src={fileUrl(u)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <video src={fileUrl(u)} className="w-full h-full object-cover" controls />
                    )}
                    <div className="absolute top-2 left-2 text-[10px] bg-white/90 px-2 py-0.5 rounded-full font-semibold text-navy-700">{u.uploader_name || "Anonymous"}</div>
                    {!u.approved && <div className="absolute top-2 right-2 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Pending</div>}
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                      {event.moderation && !u.approved && <button onClick={() => approve(u.id, true)} className="flex-1 bg-emerald-500 text-white py-1.5 rounded text-xs font-semibold inline-flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Approve</button>}
                      {event.moderation && u.approved && <button onClick={() => approve(u.id, false)} className="flex-1 bg-amber-500 text-white py-1.5 rounded text-xs font-semibold inline-flex items-center justify-center gap-1"><XIcon className="w-3 h-3" /> Hide</button>}
                      <button onClick={() => delUpload(u.id)} className="bg-red-500 text-white px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl soft-shadow text-navy-700/60">No guestbook messages yet.</div>
            ) : messages.map(m => (
              <div key={m.id} className="bg-white rounded-2xl p-5 soft-shadow flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-coral-300/20 text-coral-300 flex items-center justify-center font-bold">{(m.uploader_name || "A")[0].toUpperCase()}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-navy-700">{m.uploader_name || "Anonymous"}</span>
                    <span className="text-xs text-navy-700/50">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-2 text-navy-700/80 whitespace-pre-line">{m.message}</p>
                </div>
                <button onClick={() => delUpload(m.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-2xl space-y-6">
            <div className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
              {[
                ["allow_photos", "Allow photo uploads"],
                ["allow_videos", "Allow video uploads"],
                ["allow_messages", "Allow guestbook messages"],
                ["moderation", "Require host approval before showing uploads"],
                ["allow_rsvp", "Collect RSVPs from guests"]
              ].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between gap-3 py-3 border-b border-cream-200 last:border-0">
                  <span className="text-navy-700 font-medium">{label}</span>
                  <input type="checkbox" checked={!!event[k]} onChange={(e) => toggleSetting(k, e.target.checked)} data-testid={`settings-toggle-${k}`} className="w-5 h-5 accent-coral-300" />
                </label>
              ))}
            </div>
            <RSVPListCard eventId={id} allowRsvp={!!event.allow_rsvp} />
            <ReelBrandingCard event={event} onUpdate={setEvent} />
          </div>
        )}
      </div>
    </section>
  );
};

export default EventManagePage;
